import { ContextStore } from 'bernie-core';
import { getBexApiContext } from 'bernie-context';

import { adaptSearchCriteria, ExtendedCriteria } from 'stores/adapters/bex-api/common/search-criteria-adapter';
import { CurrentHotelStore } from 'stores/current-hotel-store';
import { getShoppingContextInput } from 'stores/adapters';
import { InfositeQueryAdapter } from 'stores/adapters/infosite-query-adapter';

import {
  PropertyAvailabilityCheck,
  PropertyTravelAdTrackingInfoInput,
  SearchOfferInput,
  SearchOfferPriceInput,
} from '__generated__/api/types';
import { MultiItemContext } from 'models/multi-item/multi-item-types';

import { PropertyInfoDetails, PropertyInfoOffersBexFeatures } from '__generated__/api';

export const getPropertyInfoInput = (
  context: ContextStore,
  propertyId: string,
  multiItemContext?: MultiItemContext,
  criteria?: ExtendedCriteria
): PropertyInfoDetails.Variables => ({
  context: getBexApiContext(context),
  propertyId,
  shoppingContext: getShoppingContextInput(multiItemContext),
  searchCriteria: adaptSearchCriteria(context, criteria, false, null),
});

export const getPropertyOffersInput = (
  context: ContextStore,
  currentHotel: CurrentHotelStore,
  multiItemContext?: MultiItemContext,
  additionalCriteria: object = {}
): PropertyInfoOffersBexFeatures.Variables => {
  const {
    searchCriteria,
    hotelId,
    selectedRatePlan,
    selectedRoomType,
    srpCurrency,
    srpDisplayPrice,
    multiItemPriceToken,
    l10n,
    referrer,
    offerTimestamp,
  } = currentHotel;

  const searchCriteriaJson = searchCriteria?.toJSON() || {};
  const criteria = {
    ...InfositeQueryAdapter.parseOut(searchCriteriaJson, l10n),
    ...searchCriteriaJson,
    ...additionalCriteria,
  };

  return {
    ...getPropertyInfoInput(context, String(hotelId)),
    searchCriteria: adaptSearchCriteria(context, criteria, false, null),
    shoppingContext: getShoppingContextInput(multiItemContext),
    travelAdTrackingInfo: getTravelAdTrackingInfo(criteria),
    searchOffer: getSearchOffer(selectedRoomType, selectedRatePlan, multiItemPriceToken, srpDisplayPrice, srpCurrency, offerTimestamp),
    referrer,
  };
};

export const getPropertyAvailabilityCheck = (
  context: ContextStore,
  criteria: ExtendedCriteria,
  propertyId: string
): PropertyAvailabilityCheck.Variables => ({
  ...getPropertyInfoInput(context, propertyId),
  searchCriteria: adaptSearchCriteria(context, criteria, false, null),
});

export const getTravelAdTrackingInfo = (criteria: any): PropertyTravelAdTrackingInfoInput | null => {
  if (criteria.trackingData) {
    return {
      beaconIssued: criteria.beaconIssued,
      candidateHmGuid: criteria.candidateHmGuid,
      position: criteria.position,
      rank: criteria.rank,
      slots: criteria.slots,
      testVersionOverride: criteria.testVersionOverride,
      trackingData: criteria.trackingData,
    };
  }

  return null;
};

export const getSearchOffer = (
  selectedRoomType: string,
  selectedRatePlan: string,
  multiItemPriceToken?: string | null,
  srpDisplayPrice?: number | null,
  srpCurrency?: string | null,
  offerTimestamp?: string | null
): SearchOfferInput | null => {
  if (selectedRoomType !== '' && selectedRatePlan !== '') {
    return {
      offerPrice: getOfferPrice(multiItemPriceToken, srpDisplayPrice, srpCurrency, offerTimestamp),
      roomTypeId: selectedRoomType,
      ratePlanId: selectedRatePlan,
    };
  }

  return null;
};

export const getOfferPrice = (
  priceToken: string,
  srpDisplayPrice: number,
  srpCurrency: string,
  offerTimestamp: string
): SearchOfferPriceInput | null => {
  if (priceToken || (srpDisplayPrice && srpCurrency)) {
    return {
      multiItemPriceToken: priceToken,
      price: {
        amount: srpDisplayPrice,
        currency: srpCurrency,
      },
      offerTimestamp,
    };
  }

  return null;
};
